import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { createClassName, createStyleHelper } from '../../utils/class-names';
import type { FCWithChildren } from '../../utils/component.interface';
import { Icon } from '../icon/icon';

import styles from './field.module.scss';

const style = createStyleHelper(styles, 'field');

export interface IFieldProps {
    className?: string;
    label?: string | JSX.Element;
    htmlFor?: string;

    hasError?: boolean;
    hasSuccess?: boolean;

    message?: string;

    info?: string | JSX.Element;
    infoOpen?: boolean;

    dark?: boolean;
    light?: boolean;
    variant?: string;
}

export const Field: FCWithChildren<IFieldProps> = ({
    className,
    children,
    label,
    htmlFor,
    message,
    info,
    infoOpen,
    hasError,
    hasSuccess,
    variant,
    dark = false,
    light = false,
}) => {
    const intl = useIntl();
    const [isInfoOpen, setIsInfoOpen] = useState(infoOpen ?? false);

    useEffect(() => {
        setIsInfoOpen(isInfoOpen);
    }, [isInfoOpen]);

    return (
        <div
            className={createClassName([
                className,
                style(undefined, {
                    'has-info-shown': !!info && isInfoOpen,
                    'has-error': hasError ?? false,
                    'has-success': hasSuccess ?? false,
                    ...(variant ? { [variant]: true } : {}),
                }),
            ])}
            data-testid="field"
        >
            {label ? (
                <label
                    className={style('label', {
                        dark,
                        light,
                    })}
                    htmlFor={htmlFor}
                >
                    {typeof label === 'string'
                        ? intl.formatMessage({
                              id: label,
                              defaultMessage: label,
                          })
                        : label}
                </label>
            ) : null}
            <div className={style('input')}>
                <div className={style('field-wrapper')}>
                    {children}

                    {info && !message ? (
                        <Icon
                            className={style('info-icon')}
                            name={'info-circle'}
                            onClick={() => setIsInfoOpen(!isInfoOpen)}
                        />
                    ) : null}
                </div>

                {message ? (
                    <label className={style('message')}>
                        {intl.formatMessage({
                            id: message,
                            defaultMessage: message,
                        })}
                    </label>
                ) : null}

                {info && isInfoOpen && !message ? (
                    <div className={style('info')}>{info}</div>
                ) : null}
            </div>
        </div>
    );
};
